/*jshint esversion: 6 */
/*jshint strict:false */
import { addDays, format, isAfter } from 'date-fns';
import Swal from 'sweetalert2';

const getDefaultState = () => {
    return {
        error: false,
        errorMessage: '',
        step: 1,
        shortOnlineServiceBooking: false,
        registration: "",
        mileage: "",
        bookingId: null,
        selectedVehicle: null,
        carNotFound: false,
        otherSelected: false,
        nonFranchiseVehicle: {
            make: '',
            model: '',
            derivative: '',
        },
        selectedDate: null,
        selectedTime: null,
        brand: window.brand ? window.brand : "",
        pricingData: {},
        manufacturerUpdate: {},
        manufacturerUpdateRetryCount: 0,
        manufacturerUpdatesSelected: [],
        haveServicePlan: null,
        fallbackPricing: false,
        motDue: false,
        motExpiryKnown: false,
        selectedOptions: {
            service: false,
            black_friday_service_and_mot: false,
            mot: false,
            system_diagnostic: false,
            courtesy_car: false,
            aircon_refresh: false,
            pollen_filter: false,
            additional_services: [],
        },
        faultDescription: '',
        customerDetails: {
            title: '',
            firstName: '',
            lastName: '',
            email: '',
            telephone: '',
            terms: '',
            marketing: ''
        },
        validator: false,
        fullFormIdentifier: 'form',
        isNonFranchiseVehicle: false,
        customerAddress: null,
        errors: null,

        waitingForServicePlan: null,
        waitingForPricing: null,
        waitingForManufacturerUpdate: null,
    };
};

const state = getDefaultState();

const mutations = {
    setStep(state, step) {
        state.step = step;
    },
    incrementStep(state) {
        state.step += 1;
    },
    decrementStep(state) {
        if (state.step > 0) {
            state.step -= 1;
        }
    },
    enableShortOnlineServiceBooking(state) {
        state.shortOnlineServiceBooking = true;
    },
    setError(state, value) {
        state.error = value;
    },
    setErrorMessage(state, message) {
        state.errorMessage = message;
    },
    setFaultDescription(state, faultDescription) {
        state.faultDescription = faultDescription;
    },
    setSelectedVehicle(state, data) {
        state.selectedVehicle = data;
    },
    setBrand(state, brand) {
        state.brand = brand;
    },
    setRegistration(state, registration) {
        state.registration = registration;
    },
    setMileage(state, mileage) {
        state.mileage = mileage;
    },
    setPricingData(state, data) {
        state.pricingData = data;
        if (typeof data.fallback !== 'undefined') {
            state.fallbackPricing = data.fallback;
        }
    },
    setHaveServicePlan(state, data) {
        state.haveServicePlan = data;
    },
    setMotDue(state, value) {
        state.motDue = value;
    },
    setMotExpiryKnown(state, value) {
        state.motExpiryKnown = value;
    },
    setSelectedDate(state, date) {
        state.selectedDate = date;
    },
    setSelectedTime(state, time) {
        state.selectedTime = time;
    },
    setOtherSelected(state, value) {
        state.otherSelected = value;
    },
    setNonFranchisedVehicle(state, data) {
        state.nonFranchiseVehicle = data;
    },
    setCarNotFound(state, value) {
        state.carNotFound = value;
    },
    setIsNonFranchiseVehicle(state, value) {
        state.isNonFranchiseVehicle = value;
    },
    setCustomerAddress(state, address) {
        state.customerAddress = address;
    },
    setBookingId(state, id) {
        state.bookingId = id;
    },
    setStateForNotMyVehicle(state) {
        state.selectedVehicle = {
            fuel_type: "",
            image_url: "",
            make: "",
            model: "",
            registration: state.registration,
            year_of_manufacture: ""
        };
        state.motExpiryKnown = false;
        state.motDue = true;
    },
    setManufacturerUpdate(state, data) {
        state.manufacturerUpdate = data;
        // Auto-select all manufacturer updates by default if none have been chosen yet
        if (data && Array.isArray(data.records) && data.records.length > 0 && state.manufacturerUpdatesSelected.length === 0) {
            state.manufacturerUpdatesSelected = data.records.map(r => ({ code: r.code, label: r.label }));
        }
    },
    toggleManufacturerUpdate(state, update) {
        if (!update || !update.code) return;
        const exists = state.manufacturerUpdatesSelected.findIndex(u => u.code === update.code);
        if (exists >= 0) {
            state.manufacturerUpdatesSelected.splice(exists, 1);
            return;
        }
        state.manufacturerUpdatesSelected.push({ code: update.code, label: update.label });
    },
    selectManufacturerUpdate(state, update) {
        if (!update || !update.code) return;
        const exists = state.manufacturerUpdatesSelected.findIndex(u => u.code === update.code);
        if (exists >= 0) {
            console.log('Manufacturer update already selected:', update.code);
            return;
        }
        console.log('Selecting manufacturer update:', update.code);
        state.manufacturerUpdatesSelected.push({ code: update.code, label: update.label });
    }
};

const actions = {
    lookupVehicle({ state, commit, dispatch }) {
        return new Promise((resolve, reject) => {
            axios({
                method: "POST",
                url: "/api/osb/vehicle-data",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    registration: state.registration,
                    mileage: state.mileage,
                },
            })
                .then((response) => {
                    if (typeof response.data.make == 'undefined' || !response.data.make) {
                        reject(new Error('Vehicle data is incomplete'));
                        return;
                    }
                    commit('setBookingId', response.data.id);
                    commit('setSelectedVehicle', response.data);
                    commit('setRegistration', response.data.registration);
                    commit('setMileage', response.data.mileage);
                    dispatch('collectServicePlan');

                    if (state.shortOnlineServiceBooking) {
                        dispatch('collectPricing');
                    }
                    dispatch('collectManufacturerUpdate');

                    resolve();
                    return;
                })
                .catch(error => {
                    console.error('Error during vehicle lookup:', error);
                    if (error.response.status == 404 || error.response.status == 400) {
                        commit('setBookingId', error.response.data.id);
                        commit('setCarNotFound', true);
                        reject(error);
                        return;
                    }

                    reject(error);
                });
        });
    },

    collectServicePlan({ commit, state }) {
        return new Promise((resolve, reject) => {
            state.waitingForServicePlan = true;
            axios({
                method: "GET",
                url: `/api/osb/service-plan/${state.registration}?bookingId=${state.bookingId}`,
                headers: {
                    Accept: "application/json",
                },
                timeout: 30000,
            })
                .then(response => {
                    state.waitingForServicePlan = false;
                    if (typeof response.data.has_service_plan != 'undefined' && response.data.has_service_plan == true) {
                        commit('setHaveServicePlan', true);
                        resolve(true);
                        return;
                    }
                    commit('setHaveServicePlan', false);
                    resolve(false);
                })
                .catch(error => {
                    state.waitingForServicePlan = false;
                    console.error(error);
                    reject(error);
                });
        });
    },

    collectPricing({ commit, state, rootState}) {
        return new Promise((resolve, reject) => {
            state.waitingForPricing = true;
            axios({
                method: "POST",
                url: `/api/osb/pricing-data`,
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    registration: state.registration,
                    mileage: state.mileage,
                    locationId: rootState['Dealers'].selectedDealer.id,
                    bookingId: state.bookingId,
                },
            }).then(response => {
                state.waitingForPricing = false;
                commit('setPricingData', response.data);

                if (typeof state.selectedVehicle.next_mot_due_date == 'undefined' || state.selectedVehicle.next_mot_due_date == null) {
                    commit('setMotExpiryKnown', false);
                    commit('setMotDue', true);
                    resolve(response);
                    return;
                }

                const dateIn60Days = addDays(new Date(), 60);
                const motExpiry = new Date(state.selectedVehicle.next_mot_due_date);

                commit('setMotExpiryKnown', true);
                commit('setMotDue', isAfter(dateIn60Days, motExpiry));
                resolve(response);
            })
            .catch(error => {
                state.waitingForPricing = false;
                console.error(error);
                reject(error);
            });
        });
    },

    collectManufacturerUpdate({ commit, state, dispatch }) {
        return new Promise((resolve, reject) => {
            state.waitingForManufacturerUpdate = true;
            axios({
                method: "POST",
                url: `/api/osb/manufacturer-update`,
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    bookingId: state.bookingId,
                },
            }).then(response => {
                if (
                    response.status == 202
                    && state.manufacturerUpdateRetryCount < 3000
                ) {
                    state.manufacturerUpdateRetryCount++;
                    setTimeout(() => {
                        dispatch('collectManufacturerUpdate');
                    }, 3000);
                    return;
                }

                state.waitingForManufacturerUpdate = false;
                commit('setManufacturerUpdate', response.data);
                resolve(response);
            }).catch(error => {
                state.waitingForManufacturerUpdate = false;
                console.error('Failed Manufacturer Update', error);
                reject(error);
            })
        })
    },

    throwFatalError() {
        Swal.fire({
            title: 'Oops...',
            html: '<p>Something went wrong!</p><a href="/contact-us" class="btn btn--primary">Contact us</a>',
            icon: 'error',
            showConfirmButton: false,
            showCloseButton: false,
            showCancelButton: false,
        });
    }
};

const getters = {
    hasManufacturerUpdateSelected(state) {
        return state.manufacturerUpdatesSelected.length > 0;
    },

    hasSystemDiagnosticSelected(state) {
        return state.selectedOptions.system_diagnostic === true;
    },

    vehicleImage(state) {
        if (state.carNotFound || !state.selectedVehicle || !state.selectedVehicle.image_url) {
            return '/images/service-and-parts/osb/image-not-available@2x.png';
        }

        return state.selectedVehicle.image_url;
    },

    bookingDate(state) {
        if (!state.selectedDate || !state.selectedTime) {
            return '';
        }

        return `${format(state.selectedDate, 'YYYY-MM-DD')} ${state.selectedTime}`;
    },

    vehicleDetailsFormatted(state, getters) {
        return {
            'registration': state.registration,
            'mileage': state.mileage,
            'make': state.otherSelected ? state.nonFranchiseVehicle.make : state.selectedVehicle.make ?? null,
            'model': state.otherSelected ? state.nonFranchiseVehicle.model : state.selectedVehicle.model ?? null,
            'derivative': state.selectedVehicle.derivative || '',
            'fuel-type': state.selectedVehicle.fuel_type,
            'next-mot-due-date': state.selectedVehicle.next_mot_due_date || 'none',
            'year-of-manufacture': state.selectedVehicle.year_of_manufacture,
            'image-url': state.selectedVehicle.image_url ? state.selectedVehicle.image_url : false,
        };
    },

    selectedOptionsArray(state) {
        let result = [];
        Object.keys(state.selectedOptions).forEach(option => {
            if (Array.isArray(state.selectedOptions[option])) {
                state.selectedOptions[option].forEach(item => {
                    if (!!item) {
                        result.push(item);
                    }
                });
                return;
            }
            if (state.selectedOptions[option] === false) {
                return;
            }
            if (typeof state.pricingData[option + '_pricing'] == 'undefined') {
                console.error('no pricing found for selected option ', option);
                return;
            }
            result.push(state.pricingData[option + '_pricing']);
        });
        return result;
    },

    subTotal(state, getters) {
        return getters.selectedOptionsArray.reduce((previousValue, selectedOption) => previousValue + selectedOption.price, 0).toFixed(2);
    },
    manufacturerUpdatesSelected(state) {
        return state.manufacturerUpdatesSelected;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
